import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { getBrandingProfile, listUserEnterpriseOrgs, type EnterpriseOrgMembership } from '../lib/enterprise-api';
import { trackTelemetryEvent } from '../lib/telemetry';

const ACTIVE_ORG_KEY = 'tension_active_org_id';

type BrandingTheme = {
  brandName?: string;
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
  supportEmail?: string;
  customDomain?: string;
};

type EnterpriseOrgContextType = {
  activeOrgId: string | null;
  setActiveOrgId: (orgId: string | null) => void;
  orgMemberships: Array<EnterpriseOrgMembership>;
  orgMembershipsLoading: boolean;
  hasActiveOrgAccess: boolean;
  brandingTheme: BrandingTheme | null;
  brandingLoading: boolean;
  activeMembership: EnterpriseOrgMembership | null;
};

const EnterpriseOrgContext = createContext<EnterpriseOrgContextType>({
  activeOrgId: null,
  setActiveOrgId: () => {},
  orgMemberships: [],
  orgMembershipsLoading: false,
  hasActiveOrgAccess: false,
  brandingTheme: null,
  brandingLoading: false,
  activeMembership: null,
});

function getInitialOrgId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

function sanitizeColor(input?: string): string | undefined {
  if (!input) return undefined;
  const v = input.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(v)) return v;
  return undefined;
}

export function EnterpriseOrgProvider({ children }: { children: React.ReactNode }) {
  const { accessToken } = useAuth();
  const location = useLocation();
  const [activeOrgId, setActiveOrgIdState] = useState<string | null>(getInitialOrgId());
  const [orgMemberships, setOrgMemberships] = useState<Array<EnterpriseOrgMembership>>([]);
  const [orgMembershipsLoading, setOrgMembershipsLoading] = useState(false);
  const [brandingTheme, setBrandingTheme] = useState<BrandingTheme | null>(null);
  const [brandingLoading, setBrandingLoading] = useState(false);

  const setActiveOrgId = (orgId: string | null) => {
    setActiveOrgIdState(orgId);
    if (typeof window === 'undefined') return;
    try {
      if (orgId) localStorage.setItem(ACTIVE_ORG_KEY, orgId);
      else localStorage.removeItem(ACTIVE_ORG_KEY);
    } catch {
      // ignore
    }
  };

  const isEnterpriseSurface = useMemo(() => {
    const path = location.pathname;
    return path.startsWith('/enterprise') || path.startsWith('/admin/enterprise') || path.startsWith('/work');
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !isEnterpriseSurface) {
      setOrgMemberships([]);
      setBrandingTheme(null);
      setOrgMembershipsLoading(false);
      return;
    }
    setOrgMembershipsLoading(true);
    listUserEnterpriseOrgs(accessToken).then((result) => {
      if (cancelled) return;
      setOrgMembershipsLoading(false);
      if (!result.success || !result.organizations) {
        console.warn('Enterprise org load failed', { path: location.pathname });
        void trackTelemetryEvent('enterprise_org_load_failed', {
          path: location.pathname,
          has_access_token: Boolean(accessToken),
        });
        setOrgMemberships([]);
        return;
      }
      setOrgMemberships(result.organizations);

      const hasCurrent = activeOrgId
        ? result.organizations.some((m) => m.organizations?.id === activeOrgId)
        : false;
      if (!hasCurrent) {
        const firstOrg = result.organizations[0]?.organizations?.id || null;
        if (activeOrgId && !firstOrg) {
          console.warn('Active enterprise org lost and no fallback membership found', {
            previousOrgId: activeOrgId,
            path: location.pathname,
          });
          void trackTelemetryEvent('enterprise_org_resolution_failed', {
            path: location.pathname,
            previous_org_id: activeOrgId,
          });
        }
        setActiveOrgId(firstOrg);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, isEnterpriseSurface, activeOrgId]);

  /** Prefer `?org=` from the URL when it matches an active membership (Command Center → workspace deep links). */
  useEffect(() => {
    if (orgMembershipsLoading || orgMemberships.length === 0) return;
    const params = new URLSearchParams(location.search);
    const urlOrg = params.get('org')?.trim() ?? '';
    if (!urlOrg) return;
    const ok = orgMemberships.some((m) => m.organizations?.id === urlOrg && m.status === 'active');
    if (!ok) {
      console.warn('Ignoring URL org without active membership', {
        urlOrg,
        path: location.pathname,
      });
      void trackTelemetryEvent('enterprise_org_url_rejected', {
        url_org: urlOrg,
        path: location.pathname,
      });
      return;
    }
    if (activeOrgId !== urlOrg) setActiveOrgId(urlOrg);
  }, [orgMemberships, orgMembershipsLoading, location.search, activeOrgId, setActiveOrgId]);

  const hasActiveOrgAccess = useMemo(() => {
    if (!activeOrgId) return false;
    return orgMemberships.some((m) => m.organizations?.id === activeOrgId && m.status === 'active');
  }, [activeOrgId, orgMemberships]);

  const activeMembership = useMemo(() => {
    if (!activeOrgId) return null;
    return orgMemberships.find((m) => m.organizations?.id === activeOrgId) || null;
  }, [activeOrgId, orgMemberships]);

  useEffect(() => {
    let cancelled = false;
    if (!accessToken || !isEnterpriseSurface || !activeOrgId || !hasActiveOrgAccess) {
      setBrandingTheme(null);
      return;
    }
    setBrandingLoading(true);
    getBrandingProfile(activeOrgId, accessToken).then((result) => {
      if (cancelled) return;
      setBrandingLoading(false);
      if (!result.success || !result.branding) {
        setBrandingTheme(null);
        return;
      }
      const b = result.branding;
      setBrandingTheme({
        brandName: typeof b.brand_name === 'string' ? b.brand_name : undefined,
        logoUrl: typeof b.logo_url === 'string' ? b.logo_url : undefined,
        supportEmail: typeof b.support_email === 'string' ? b.support_email : undefined,
        customDomain: typeof b.custom_domain === 'string' ? b.custom_domain : undefined,
        primaryColor: sanitizeColor(typeof b.primary_color === 'string' ? b.primary_color : undefined),
        accentColor: sanitizeColor(typeof b.accent_color === 'string' ? b.accent_color : undefined),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken, isEnterpriseSurface, activeOrgId, hasActiveOrgAccess]);

  const value: EnterpriseOrgContextType = {
    activeOrgId,
    setActiveOrgId,
    orgMemberships,
    orgMembershipsLoading,
    hasActiveOrgAccess,
    brandingTheme,
    brandingLoading,
    activeMembership,
  };

  return <EnterpriseOrgContext.Provider value={value}>{children}</EnterpriseOrgContext.Provider>;
}

export function useEnterpriseOrg() {
  return useContext(EnterpriseOrgContext);
}
